import { useFunnel, useAREDashboard } from '../hooks/useARE'
import { BarChart3, TrendingUp, Users, Star, Mail, MousePointer, CheckCircle } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

const FUNNEL_STEPS = [
  { key: 'contacts_imported',     label: 'Contacts importés',  color: '#6366f1', icon: Users },
  { key: 'contacts_scored',       label: 'Contacts scorés',    color: '#8b5cf6', icon: BarChart3 },
  { key: 'promoters_identified',  label: 'Promoteurs',         color: '#0ea5e9', icon: Star },
  { key: 'emails_sent',           label: 'Emails envoyés',     color: '#10b981', icon: Mail },
  { key: 'emails_opened',         label: 'Ouverts',            color: '#f59e0b', icon: Mail },
  { key: 'links_clicked',         label: 'Clics lien',         color: '#f97316', icon: MousePointer },
  { key: 'reviews_published',     label: 'Avis publiés',       color: '#dc2626', icon: CheckCircle },
]

export function AnalyticsPage() {
  const { data: funnel, isLoading } = useFunnel()
  const { data: dash } = useAREDashboard()

  const maxVal = funnel
    ? Math.max(...FUNNEL_STEPS.map(s => funnel[s.key] || 0), 1)
    : 1

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Analytics</h1>
        <p className="text-ink-muted mt-1">Funnel de conversion et métriques de performance</p>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Taux d'ouverture", value: `${dash?.open_rate ?? 0}%`, sub: "emails ouverts", color: "text-amber-600 bg-amber-50" },
          { label: "Taux de conversion", value: `${dash?.conversion_rate ?? 0}%`, sub: "envoi → avis", color: "text-emerald-600 bg-emerald-50" },
          { label: "Avis générés", value: dash?.total_reviewed ?? 0, sub: "ce mois", color: "text-blue-600 bg-blue-50" },
          { label: "Feedbacks internes", value: dash?.new_feedbacks ?? 0, sub: "non traités", color: "text-red-600 bg-red-50" },
        ].map(kpi => (
          <div key={kpi.label} className="bg-white rounded-card border border-ink-border p-5">
            <p className="text-sm text-ink-muted">{kpi.label}</p>
            <p className="font-serif text-2xl font-bold text-ink tracking-tight mt-1">{kpi.value}</p>
            <p className="text-xs text-ink-faint mt-0.5">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Funnel Visualization */}
      <div className="bg-white rounded-card border border-ink-border p-6">
        <h2 className="text-base font-semibold text-ink mb-6">Funnel de conversion — 30 jours</h2>

        {isLoading ? (
          <div className="h-64 flex items-center justify-center text-ink-faint">Chargement…</div>
        ) : !funnel ? (
          <div className="h-64 flex items-center justify-center text-ink-faint">Aucune donnée</div>
        ) : (
          <div className="space-y-3">
            {FUNNEL_STEPS.map((step, i) => {
              const val = funnel[step.key] || 0
              const pct = maxVal > 0 ? (val / maxVal) * 100 : 0
              const convRate = i > 0 && funnel[FUNNEL_STEPS[0].key] > 0
                ? ((val / funnel[FUNNEL_STEPS[0].key]) * 100).toFixed(1)
                : '100'

              return (
                <div key={step.key} className="flex items-center gap-4">
                  <div className="w-40 flex items-center gap-2 flex-shrink-0">
                    <step.icon size={16} style={{ color: step.color }} />
                    <span className="text-sm text-ink truncate">{step.label}</span>
                  </div>
                  <div className="flex-1 bg-paper-warm rounded-full h-8 relative overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 flex items-center justify-end pr-3"
                      style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: step.color }}
                    >
                      {pct > 15 && (
                        <span className="text-xs font-semibold text-white">{val}</span>
                      )}
                    </div>
                    {pct <= 15 && (
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-ink">
                        {val}
                      </span>
                    )}
                  </div>
                  <div className="w-16 text-right flex-shrink-0">
                    <span className="text-xs font-medium text-ink-muted">{convRate}%</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Step-by-step conversion rates */}
      {funnel && (
        <div className="bg-white rounded-card border border-ink-border p-6">
          <h2 className="text-base font-semibold text-ink mb-4">Taux de conversion par étape</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { from: 'contacts_scored', to: 'promoters_identified', label: 'Scoré → Promoteur' },
              { from: 'promoters_identified', to: 'emails_sent', label: 'Promoteur → Envoyé' },
              { from: 'emails_sent', to: 'emails_opened', label: 'Envoyé → Ouvert' },
              { from: 'emails_opened', to: 'links_clicked', label: 'Ouvert → Cliqué' },
              { from: 'links_clicked', to: 'reviews_published', label: 'Cliqué → Avis publié' },
              { from: 'emails_sent', to: 'reviews_published', label: 'Envoyé → Avis (global)' },
            ].map(({ from, to, label }) => {
              const rate = funnel[from] > 0
                ? ((funnel[to] / funnel[from]) * 100).toFixed(1)
                : '0.0'
              return (
                <div key={label} className="bg-paper rounded-lg p-4">
                  <p className="text-xs text-ink-muted mb-1">{label}</p>
                  <p className="text-xl font-bold text-ink">{rate}%</p>
                  <p className="text-[11px] text-ink-faint">{funnel[to]} / {funnel[from]}</p>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
